import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Box,
  Paper,
  CircularProgress,
  Divider,
  Stack,
  GlobalStyles,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import { persistSession } from "../utils/session";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { motion } from "framer-motion";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  blue: "#1e4b81",
  blueDark: "#102840",
  white: "#ffffff",
  bg: "#f7f9fc",
  surface: "#ffffff",
  text: "#1d2f43",
  secondary: "#64748b",
  muted: "#4a5568",
  border: "rgba(16, 40, 64, 0.10)",
  borderSoft: "rgba(16, 40, 64, 0.07)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: { xs: "12px", sm: "13px" },
    bgcolor: COLORS.white,
    minHeight: { xs: 42, sm: 44 },
    boxShadow: "none",
    "& fieldset": {
      borderColor: "rgba(16,40,64,0.14)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(211,125,54,0.46)",
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.orange,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input": {
    py: { xs: 1.05, sm: 1.15 },
    fontSize: { xs: "0.84rem", sm: "0.88rem" },
    fontWeight: 650,
    color: COLORS.blueDark,
  },
};

const InfoItem = ({ children }) => (
  <Stack direction="row" spacing={1} alignItems="flex-start">
    <CheckCircleOutlineIcon
      sx={{ color: COLORS.orange, fontSize: 18, mt: "2px", flexShrink: 0 }}
    />
    <Typography
      sx={{
        color: "rgba(255,255,255,0.78)",
        fontSize: "0.84rem",
        lineHeight: 1.45,
        fontWeight: 650,
      }}
    >
      {children}
    </Typography>
  </Stack>
);

const LogoBlock = ({ compact = false }) => (
  <Stack
    direction="row"
    spacing={compact ? 1 : 1.25}
    alignItems="center"
    justifyContent={compact ? "center" : "flex-start"}
    sx={{ minWidth: 0 }}
  >
    <Box
      sx={{
        width: compact ? 44 : 48,
        height: compact ? 44 : 48,
        borderRadius: compact ? "15px" : "16px",
        bgcolor: COLORS.white,
        border: compact ? `1px solid ${COLORS.borderSoft}` : "none",
        boxShadow: compact ? "0 10px 24px rgba(16,40,64,0.08)" : "none",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src="/images/logo_simbolo.png"
        alt="DQPa"
        sx={{
          width: compact ? 34 : 36,
          height: compact ? 34 : 36,
          objectFit: "contain",
        }}
      />
    </Box>

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: compact ? COLORS.blueDark : COLORS.white,
          fontSize: compact ? "1.32rem" : "1.35rem",
          fontWeight: 950,
          letterSpacing: "-0.045em",
          lineHeight: 1,
        }}
      >
        DQPa
      </Typography>
      <Typography
        sx={{
          color: compact ? COLORS.secondary : "rgba(255,255,255,0.64)",
          fontSize: compact ? "0.62rem" : "0.68rem",
          fontWeight: 800,
          letterSpacing: "0.055em",
          textTransform: "uppercase",
          mt: 0.35,
          whiteSpace: { xs: "normal", sm: "nowrap" },
        }}
      >
        Data Quality Process Assessment
      </Typography>
    </Box>
  </Stack>
);

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Por favor, complete todos los campos.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axiosInstance.post(API_ENDPOINTS.auth.login, {
        email,
        password,
      });

      const token =
        response.data?.token ||
        response.data?.accessToken ||
        response.data?.jwt ||
        response.data?.data?.token ||
        response.data?.user?.token;

      if (!token) {
        throw new Error("El servidor no devolvió token de sesión.");
      }

      const session = persistSession({
        token,
        user: response.data?.user || response.data?.usuario,
        remember: rememberMe,
      });

      if (!session?.id) {
        throw new Error("No se ha podido identificar al usuario autenticado.");
      }

      setTimeout(() => {
        setLoading(false);
        navigate(
          session.role === "ROLE_ADMIN"
            ? "/admin-dashboard"
            : "/mis-autoevaluaciones"
        );
      }, 450);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Credenciales incorrectas o error en el servidor."
      );
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            fontSize: "14.5px",
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            backgroundColor: COLORS.bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "#root": {
            minHeight: "100vh",
            overflowX: "hidden",
          },
          "*": {
            boxSizing: "border-box",
          },
          "@media (max-width: 600px)": {
            html: { fontSize: "14px" },
          },
        }}
      />

      <Box
        component="main"
        sx={{
          minHeight: { xs: "100svh", lg: "100vh" },
          width: "100%",
          overflowX: "hidden",
          overflowY: { xs: "auto", lg: "hidden" },
          bgcolor: COLORS.bg,
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <Grid container sx={{ minHeight: { xs: "100svh", lg: "100vh" } }}>
          <Grid
            item
            xs={12}
            lg={5.6}
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              justifyContent: "center",
              px: { lg: 5.5, xl: 7 },
              py: 4,
              bgcolor: COLORS.blueDark,
              color: COLORS.white,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0.12,
                backgroundImage:
                  "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                right: -120,
                bottom: -120,
                width: 300,
                height: 300,
                borderRadius: "50%",
                bgcolor: "rgba(211,125,54,0.20)",
              }}
            />

            <motion.div
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              style={{
                width: "100%",
                maxWidth: 460,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Stack spacing={3}>
                <LogoBlock />

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { lg: "2.35rem", xl: "2.65rem" },
                      lineHeight: 1.06,
                      letterSpacing: "-0.05em",
                      fontWeight: 950,
                      maxWidth: 430,
                    }}
                  >
                    Acceda a su evaluación de gobierno y gestión de{" "}
                    <Box component="span" sx={{ color: COLORS.orange }}>
                      datos
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.55,
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.92rem",
                      lineHeight: 1.58,
                      maxWidth: 420,
                    }}
                  >
                    Entre en su espacio privado para consultar autoevaluaciones,
                    resultados y propuestas de mejora.
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "22px",
                    bgcolor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <Stack spacing={1.15}>
                    <InfoItem>
                      Evaluaciones alineadas con UNE 0077, 0078, 0079 y 0080.
                    </InfoItem>
                    <InfoItem>
                      Resultados claros para perfiles técnicos y de dirección.
                    </InfoItem>
                    <InfoItem>Propuestas de mejora priorizadas.</InfoItem>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                  {["Gobierno", "Gestión", "Calidad", "Madurez"].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        px: 1.2,
                        py: 0.58,
                        borderRadius: "999px",
                        bgcolor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.74)",
                        fontSize: "0.68rem",
                        fontWeight: 850,
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </motion.div>
          </Grid>

          <Grid
            item
            xs={12}
            lg={6.4}
            sx={{
              minHeight: { xs: "100svh", lg: "100vh" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 1.6, sm: 2.5, md: 4 },
              py: { xs: 2.4, sm: 3, md: 4, lg: 3 },
              bgcolor: COLORS.bg,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: "easeOut", delay: 0.05 }}
              style={{ width: "100%", maxWidth: 390 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 2.7 },
                  borderRadius: { xs: "22px", sm: "24px" },
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: COLORS.white,
                  boxShadow: "0 18px 50px rgba(16,40,64,0.08)",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    justifyContent: "center",
                    mb: { xs: 1.8, sm: 2 },
                  }}
                >
                  <LogoBlock compact />
                </Box>

                <Box sx={{ textAlign: "center", mb: { xs: 1.9, sm: 2.15 } }}>
                  <Box
                    sx={{
                      width: { xs: 38, sm: 42 },
                      height: { xs: 38, sm: 42 },
                      mx: "auto",
                      mb: 1.15,
                      borderRadius: "15px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(30,75,129,0.06)",
                      color: COLORS.blue,
                      border: "1px solid rgba(30,75,129,0.09)",
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
                  </Box>

                  <Typography
                    component="h2"
                    sx={{
                      color: COLORS.blueDark,
                      fontWeight: 950,
                      fontSize: { xs: "1.3rem", sm: "1.55rem" },
                      letterSpacing: "-0.04em",
                      lineHeight: 1.1,
                      mb: 0.65,
                    }}
                  >
                    Iniciar sesión
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.secondary,
                      fontSize: { xs: "0.8rem", sm: "0.84rem" },
                      lineHeight: 1.45,
                    }}
                  >
                    Acceda a sus autoevaluaciones y resultados.
                  </Typography>
                </Box>

                <Box component="form" noValidate onSubmit={handleLogin}>
                  <Stack spacing={{ xs: 1.3, sm: 1.45 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 850,
                          mb: 0.55,
                          color: COLORS.blueDark,
                          fontSize: "0.78rem",
                        }}
                      >
                        Correo electrónico
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@empresa.com"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlinedIcon
                                sx={{ color: COLORS.secondary, fontSize: 19 }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 850,
                          mb: 0.55,
                          color: COLORS.blueDark,
                          fontSize: "0.78rem",
                        }}
                      >
                        Contraseña
                      </Typography>
                      <TextField
                        required
                        fullWidth
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon
                                sx={{ color: COLORS.secondary, fontSize: 19 }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                                sx={{
                                  color: COLORS.secondary,
                                  "&:hover": {
                                    color: COLORS.orange,
                                    bgcolor: "rgba(211,125,54,0.08)",
                                  },
                                }}
                              >
                                {showPassword ? (
                                  <VisibilityOff fontSize="small" />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    </Box>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent={{ xs: "center", sm: "flex-end" }}
                    >
                      <Link
                        component={RouterLink}
                        to="/forgot-password"
                        sx={{
                          textDecoration: "none",
                          color: COLORS.blue,
                          fontWeight: 850,
                          fontSize: "0.76rem",
                          "&:hover": { color: COLORS.orange },
                        }}
                      >
                        ¿Olvidaste tu contraseña?
                      </Link>
                    </Stack>

                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          borderRadius: "13px",
                          fontWeight: 650,
                          fontSize: "0.8rem",
                          py: 0.6,
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      endIcon={!loading ? <ArrowForwardIcon /> : null}
                      sx={{
                        mt: 0.15,
                        py: 1.12,
                        minHeight: 44,
                        borderRadius: "13px",
                        textTransform: "none",
                        fontWeight: 900,
                        fontSize: "0.9rem",
                        bgcolor: COLORS.blue,
                        boxShadow: "0 12px 26px rgba(30,75,129,0.18)",
                        transition: "all .2s ease",
                        "&:hover": {
                          bgcolor: COLORS.blueDark,
                          boxShadow: "0 16px 34px rgba(16,40,64,0.24)",
                          transform: "translateY(-1px)",
                        },
                        "&.Mui-disabled": {
                          bgcolor: "rgba(30,75,129,0.45)",
                          color: COLORS.white,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={21} color="inherit" />
                      ) : (
                        "Iniciar sesión"
                      )}
                    </Button>
                  </Stack>
                </Box>

                <Divider sx={{ my: { xs: 1.8, sm: 2.1 }, borderColor: COLORS.borderSoft }} />

                <Typography
                  align="center"
                  sx={{
                    color: COLORS.secondary,
                    fontSize: "0.82rem",
                    lineHeight: 1.4,
                  }}
                >
                  ¿No tienes cuenta?{" "}
                  <Link
                    component={RouterLink}
                    to="/register"
                    sx={{
                      textDecoration: "none",
                      color: COLORS.orange,
                      fontWeight: 900,
                      "&:hover": { color: COLORS.orangeDark },
                    }}
                  >
                    Crear cuenta
                  </Link>
                </Typography>
              </Paper>

              <Typography
                align="center"
                sx={{
                  mt: 1.45,
                  px: { xs: 1, sm: 0 },
                  color: COLORS.secondary,
                  fontSize: "0.7rem",
                  lineHeight: 1.45,
                }}
              >
                Al iniciar sesión, aceptas nuestra{" "}
                <Link
                  component={RouterLink}
                  to="/politica"
                  sx={{
                    color: COLORS.blue,
                    fontWeight: 800,
                    textDecoration: "none",
                    "&:hover": {
                      color: COLORS.orange,
                      textDecoration: "underline",
                    },
                  }}
                >
                  Política de Privacidad
                </Link>
                .
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default LoginPage;
